import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { DepartmentType } from '@enums/DepartmentType';

export function useOnboarding() {
    const store = useStore();
    const plan = computed(() => store.getters['Departments/GET_ACTIVE_PLAN']);
    const user = computed(() => store.getters['UserStore/GET_USER']);

    const showOnboarding = ref(false);
    const onboarding = ref(null);

    watch(
        [user, plan],
        ([currentUser, currentPlan]) => {
            if (!currentUser?.onboarding || !currentPlan) {
                return;
            }

            if (currentPlan.type === DepartmentType.Employ) {
                if (
                    currentPlan.name === 'Bas' &&
                    currentUser.onboarding?.employ_bas === false &&
                    currentUser.onboarding?.employ_bas_plus === false
                ) {
                    onboarding.value = {
                        type: 'employ_bas',
                        heading: 'Kom igång med ditt konto',
                        steps: [
                            {
                                header: 'Välkommen till vår snabbguide!',
                                text: 'Allt du behöver för att hålla koll på att dina medarbetare har rätt kompetens vid rätt tidpunkt.',
                                img: '/images/onboarding/start.jpg',
                            },
                            {
                                header: 'Lägg till dina medarbetare',
                                text: 'För att lägga till dina medarbetare, gå till "Medarbetare" i menyn. Här kan du lägga till medarbetare, en åt gången eller flera samtidigt med hjälp av filuppladdning. Du kan även bjuda in medarbetare via e-post, de kommer då att få en inbjudan till att registrera sig i systemet.',
                                img: '/images/onboarding/add-employee.jpg',
                            },
                            {
                                header: 'Skapa egna interna kompetenser',
                                text: 'Du har möjlighet att skapa egna interna kompetenser. För att skapa kompetenser, gå till "Kompetenser" i menyn. Du kan ange kompetensnamnet, en beskrivning, och giltighetstid.',
                                img: '/images/onboarding/create-competence.jpg',
                            },
                            {
                                header: 'Certifiera medarbetare med interna eller externa kompetenser',
                                text: 'Under "Utfärda certifikat" kan du lägga till både interna och externa kompetenser på medarbetare. Du väljer vilka kompetenser de ska certifieras med och detaljer om certifieringen. Du kan också ladda upp certifikatskopia om det finns.',
                                img: '/images/onboarding/certify-employees.jpg',
                            },
                        ],
                    };
                } else if (
                    currentPlan.name === 'Plus' &&
                    currentUser.onboarding?.employ_bas === false &&
                    currentUser.onboarding?.employ_plus === false &&
                    currentUser.onboarding?.employ_bas_plus === false
                ) {
                    onboarding.value = {
                        type: 'employ_bas_plus',
                        heading: 'Kom igång med ditt konto',
                        steps: [
                            {
                                header: 'Välkommen till vår snabbguide!',
                                text: 'Allt du behöver för att hålla koll på att dina medarbetare har rätt kompetens vid rätt tidpunkt.',
                                img: '/images/onboarding/start.jpg',
                            },
                            {
                                header: 'Lägg till dina medarbetare',
                                text: 'För att lägga till dina medarbetare, gå till "Medarbetare" i menyn. Här kan du lägga till medarbetare, en åt gången eller flera samtidigt med hjälp av filuppladdning. Du kan även bjuda in medarbetare via e-post, de kommer då att få en inbjudan till att registrera sig i systemet.',
                                img: '/images/onboarding/add-employee.jpg',
                            },
                            {
                                header: 'Team',
                                text: 'Samla dina medarbetare i team för att få överblick över kompetensen i de olika delarna av organisationen.',
                                img: '/images/onboarding/create-team.jpg',
                            },
                            {
                                header: 'Skapa egna interna kompetenser',
                                text: 'Du har möjlighet att skapa egna interna kompetenser. Några interna kompetenser kan vara: Introduktion av nyanställd, Mätteknik, Tidredovisning etc. För att skapa kompetenser, gå till "Kompetenser" i menyn. Du kan ange kompetensens namn, en beskrivning av dess innehåll och eventuell giltighetstid.',
                                img: '/images/onboarding/create-competence.jpg',
                            },
                            {
                                header: 'Roller',
                                text: `<p>Roller kan innehålla både era interna kompetenser och externa kompetenser.</p>
    <p>Genom att koppla dina medarbetare till en roll kan du se och planera utbildningsbehov för alla i den rollen. Du håller även reda på om kompetens som krävs i rollen håller på att förfalla.</p>`,
                                img: '/images/onboarding/create-expertise.jpg',
                            },
                            {
                                header: 'Utfärda interna och externa kompetenser',
                                text: 'Under "Utfärda certifikat" kan du lägga till både interna och externa kompetenser på medarbetare. Du väljer vilka kompetenser de ska certifieras med och detaljer om certifieringen. Du kan också ladda upp certifikatskopia om det finns.',
                                img: '/images/onboarding/certify-employees.jpg',
                            },
                        ],
                    };
                } else if (
                    currentPlan.name === 'Plus' &&
                    currentUser.onboarding?.employ_plus === false &&
                    currentUser.onboarding?.employ_bas_plus === false
                ) {
                    onboarding.value = {
                        type: 'employ_plus',
                        heading: 'Full koll med plus',
                        steps: [
                            {
                                header: 'Välkommen till Plus!',
                                text: 'Vi ska kort gå igenom vad som är nytt för dig i ditt Plus-konto.',
                                img: '/images/onboarding/start.jpg',
                            },
                            {
                                header: 'Team',
                                text: 'Samla dina medarbetare i team för att få överblick över kompetensen i de olika delarna av organisationen.',
                                img: '/images/onboarding/create-team.jpg',
                            },
                            {
                                header: 'Roller',
                                text: `<p>Roller kan innehålla både era interna kompetenser och externa kompetenser.</p>
    <p>Genom att koppla dina medarbetare till en roll kan du se och planera utbildningsbehov för alla i den rollen. Du håller även reda på om kompetens som krävs i rollen håller på att förfalla.</p>`,
                                img: '/images/onboarding/create-expertise.jpg',
                            },
                            {
                                header: 'Full koll med Plus',
                                text: 'När du byggt upp din organisation med team och roller kommer vi att hjälpa dig hålla koll på allt som händer och så att arbetet kan flyta på och kompetenserna hålls uppdaterade.',
                                img: '/images/onboarding/certificate-alerts.jpg',
                            },
                        ],
                    };
                } else if (
                    currentPlan.name === 'Premium' &&
                    currentUser.onboarding?.employ_premium === false &&
                    currentUser.onboarding?.employ_bas === false &&
                    currentUser.onboarding?.employ_plus === false &&
                    currentUser.onboarding?.employ_bas_plus === false
                ) {
                    onboarding.value = {
                        type: 'employ_premium',
                        heading: 'Full koll med Premium',
                        steps: [
                            {
                                header: 'Välkommen till vår snabbguide!',
                                text: 'Allt du behöver för att hålla koll på att dina medarbetare har rätt kompetens vid rätt tidpunkt.',
                                img: '/images/onboarding/start.jpg',
                            },
                            {
                                header: 'Lägg till dina medarbetare',
                                text: 'För att lägga till dina medarbetare, gå till "Medarbetare" i menyn. Här kan du lägga till medarbetare, en åt gången eller flera samtidigt med hjälp av filuppladdning. Du kan även bjuda in medarbetare via e-post, de kommer då att få en inbjudan till att registrera sig i systemet.',
                                img: '/images/onboarding/add-employee.jpg',
                            },
                            {
                                header: 'Team',
                                text: 'Samla dina medarbetare i team för att få överblick över kompetensen i de olika delarna av organisationen.',
                                img: '/images/onboarding/create-team.jpg',
                            },
                            {
                                header: 'Skapa egna interna kompetenser',
                                text: 'Du har möjlighet att skapa egna interna kompetenser. Några interna kompetenser kan vara: Introduktion av nyanställd, Mätteknik, Tidredovisning etc. För att skapa kompetenser, gå till "Kompetenser" i menyn. Du kan ange kompetensens namn, en beskrivning av dess innehåll och eventuell giltighetstid.',
                                img: '/images/onboarding/create-competence.jpg',
                            },
                            {
                                header: 'Roller',
                                text: `
                                    <p>Roller kan innehålla både era interna kompetenser och externa kompetenser.</p>
                                    <p>Genom att koppla dina medarbetare till en roll kan du se och planera utbildningsbehov för alla i den rollen. Du håller även reda på om kompetens som krävs i rollen håller på att förfalla.</p>
                                `,
                                img: '/images/onboarding/create-expertise.jpg',
                            },
                            {
                                header: 'Utfärda interna och externa kompetenser',
                                text: 'Under "Utfärda certifikat" kan du lägga till både interna och externa kompetenser på medarbetare. Du väljer vilka kompetenser de ska certifieras med och detaljer om certifieringen. Du kan också ladda upp certifikatskopia om det finns.',
                                img: '/images/onboarding/certify-employees.jpg',
                            },
                            {
                                header: 'Planera kompetensbehov',
                                text: `
                                    <p>Ställ in hur många du behöver av respektive roll i varje team för att enkelt kunna hålla koll på när det är dags att kompetensutveckla eller fördela medarbetare över team.</p>
                                `,
                                img: '/images/onboarding/plan-competence.png',
                            },
                            {
                                header: 'Pausa medarbetare',
                                text: `
                                    <p>När någon av dina medarbetares kompetens under en period inte kan räknas som en tillgång, till exempel under semestern, så kan du pausa medarbetaren.</p>
                                `,
                                img: '/images/onboarding/pause-employee.png',
                            },
                            {
                                header: 'Full koll med Premium',
                                text: `
                                    <p>När du ställt in dina kompetensbehov så hjälper vi dig att hålla koll på under- och överskott av kompetenser så att du kan ligga steget före med ditt företags kompetensfördelning över tid.</p>
                                `,
                                img: '/images/onboarding/overview-competeces.png',
                            },
                        ],
                    };
                } else if (
                    currentPlan.name === 'Premium' &&
                    currentUser.onboarding?.employ_premium_plus === false &&
                    currentUser.onboarding?.employ_premium === false
                ) {
                    onboarding.value = {
                        type: 'employ_premium_plus',
                        heading: 'Full koll med Premium',
                        steps: [
                            {
                                header: 'Välkommen till Premium!',
                                text: `
                                    <p>Vi ska kort gå igenom vad som är nytt för dig i ditt Premium-konto.</p>
                                `,
                                img: '/images/onboarding/start.jpg',
                            },
                            {
                                header: 'Planera kompetensbehov',
                                text: `
                                    <p>Ställ in hur många du behöver av respektive roll i varje team för att enkelt kunna hålla koll på när det är dags att kompetensutveckla eller fördela medarbetare över team.</p>
                                `,
                                img: '/images/onboarding/plan-competence.png',
                            },
                            {
                                header: 'Pausa medarbetare',
                                text: `
                                    <p>När någon av dina medarbetares kompetens under en period inte kan räknas som en tillgång, till exempel under semestern, så kan du pausa medarbetaren.</p>
                                `,
                                img: '/images/onboarding/pause-employee.png',
                            },
                            {
                                header: 'Full koll med Premium',
                                text: `
                                    <p>När du ställt in dina kompetensbehov så hjälper vi dig att hålla koll på under- och överskott av kompetenser så att du kan ligga steget före med ditt företags kompetensfördelning över tid.</p>
                                `,
                                img: '/images/onboarding/overview-competeces.png',
                            },
                        ],
                    };
                }
            }
            //------- educate -------
            else if (currentPlan.type === DepartmentType.Educate) {
                if (currentPlan.name === 'Bas' && currentUser.onboarding?.educate_bas === false) {
                    onboarding.value = {
                        type: 'educate_bas',
                    };
                }
            } else {
                onboarding.value = null;
            }

            showOnboarding.value = onboarding.value !== null;
        },
        { immediate: true }
    );

    return {
        showOnboarding,
        onboarding,
    };
}
